import { useState, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Smartphone,
  Coins,
  CreditCard,
  UtensilsCrossed,
  Languages,
  Shield,
  QrCode,
  MapPin,
  Store,
} from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { useGeolocation } from '../hooks/useGeolocation'
import { isYokosoPointsEnabled } from '../lib/features'
import { markLocationAsked } from '../lib/locationAsk'

interface Slide {
  icon1: React.ReactNode
  icon2: React.ReactNode
  title: string
  description: string
}

/**
 * #1984 **見せるのは「いま実際にできること」だけ。**
 *
 * 以前の 1 枚目は「デジタル円を即座にチャージ」、2 枚目は「カメラをメニューに
 * かざすと AI が料理を見せる」だった。どちらも**本番には存在しない**:
 *
 * - チャージは本番で塞がれている (#1446 — ポイント自体が無く、ボタン・ルート・
 *   API のすべてが閉じている)。JPYC モードも #1740 で本番では無効
 * - 顧客画面から AI を呼ぶコードは 1 行も無い (AI はオーナーのメニュー登録用)
 *
 * 最初の 2 枚で、できないことを約束していた。実際の決済 (QR を読んでアプリ内
 * カード決済) と、実際の価値 (10 言語のメニューとアレルギー表示) に差し替える。
 */
const SLIDE_ICONS = [
  {
    icon1: <QrCode className="h-16 w-16 text-navy" strokeWidth={1.5} />,
    icon2: <CreditCard className="h-12 w-12 text-gold" strokeWidth={1.5} />,
  },
  {
    icon1: <Languages className="h-16 w-16 text-navy" strokeWidth={1.5} />,
    icon2: <UtensilsCrossed className="h-12 w-12 text-matcha" strokeWidth={1.5} />,
  },
  {
    icon1: <Shield className="h-16 w-16 text-navy" strokeWidth={1.5} />,
    icon2: <QrCode className="h-12 w-12 text-yokoso-red" strokeWidth={1.5} />,
  },
  {
    icon1: <MapPin className="h-16 w-16 text-navy" strokeWidth={1.5} />,
    icon2: <Store className="h-12 w-12 text-matcha" strokeWidth={1.5} />,
  },
]

const SWIPE_THRESHOLD = 50

export default function Onboarding() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const [current, setCurrent] = useState(0)
  const { requestLocation } = useGeolocation()
  const touchStartX = useRef(0)
  const touchEndX = useRef(0)

  const slides: Slide[] = useMemo(() => [
    {
      ...SLIDE_ICONS[0],
      title: t('onboarding.slide1.title'),
      description: t('onboarding.slide1.desc'),
    },
    {
      ...SLIDE_ICONS[1],
      title: t('onboarding.slide2.title'),
      description: t('onboarding.slide2.desc'),
    },
    {
      ...SLIDE_ICONS[2],
      title: t('onboarding.slide3.title'),
      description: t('onboarding.slide3.desc'),
    },
    // #1984 チャージは**使える環境でだけ**見せる。本番では機能ごと閉じている
    // (#1446) ので出さない。対応したらフラグが立ち、ここが自動で復活する
    // — 将来また文言を足し直さなくて済む。
    ...(isYokosoPointsEnabled() ? [{
      icon1: <Smartphone className="h-16 w-16 text-navy" strokeWidth={1.5} />,
      icon2: <Coins className="h-12 w-12 text-gold" strokeWidth={1.5} />,
      title: t('onboarding.charge.title'),
      description: t('onboarding.charge.desc'),
    }] : []),
    // 最後は位置情報。**何に使うかを読ませてから**聞く (いきなりダイアログを出すと
    // 断られやすく、断られると後から覆しにくい)。HOME に着く前に決まるので、
    // 最初の HOME から近くのお店・イベントが出せる。
    {
      ...SLIDE_ICONS[3],
      title: t('onboarding.slide4.title'),
      description: t('onboarding.slide4.desc'),
    },
  ], [t])

  const finishOnboarding = useCallback(() => {
    localStorage.setItem('hasSeenOnboarding', 'true')
    navigate('/login')
  }, [navigate])

  // 位置情報の枚で押したときだけ「聞いた」ことを残す (スキップした人には HOME で聞く)
  const answerLocation = (allow: boolean) => {
    markLocationAsked()
    if (allow) requestLocation()
    finishOnboarding()
  }

  const goNext = () => {
    if (current < slides.length - 1) {
      setCurrent(current + 1)
    } else {
      finishOnboarding()
    }
  }

  const goPrev = () => {
    if (current > 0) {
      setCurrent(current - 1)
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    const deltaX = touchStartX.current - touchEndX.current
    if (deltaX > SWIPE_THRESHOLD) {
      goNext()
    } else if (deltaX < -SWIPE_THRESHOLD) {
      goPrev()
    }
  }

  const slide = slides[current]
  const isLast = current === slides.length - 1
  // 最後の 1 枚は位置情報。ボタンを「現在地を許可する」/「あとで」に差し替える
  const isLocationSlide = isLast

  return (
    <div
      className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Illustration Area - Top 60% */}
      <div className="flex flex-1 items-center justify-center px-8 pt-12">
        <div
          key={current}
          className="animate-fade-in flex h-72 w-full flex-col items-center justify-center rounded-xl bg-white shadow-card"
        >
          <div className="flex items-end gap-4">
            {slide.icon1}
            {slide.icon2}
          </div>
        </div>
      </div>

      {/* Text Content */}
      <div className="px-8 pb-4 pt-6">
        <h2
          key={`title-${current}`}
          className="animate-fade-in font-display text-2xl font-bold text-navy"
        >
          {slide.title}
        </h2>
        <p
          key={`desc-${current}`}
          className="animate-fade-in mt-3 font-body text-base leading-relaxed text-text-secondary"
          style={{ animationDelay: '0.1s', animationFillMode: 'both' }}
        >
          {slide.description}
        </p>
      </div>

      {/* Page Indicator */}
      <div className="flex items-center justify-center gap-2 py-4">
        {slides.map((_, i) => (
          <div
            key={i}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === current
                ? 'w-6 bg-yokoso-red'
                : 'w-2 bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Bottom Actions */}
      <div className="px-6 pb-8">
        <button
          onClick={isLocationSlide ? () => answerLocation(true) : goNext}
          className="tap-effect h-14 w-full rounded-xl bg-yokoso-red font-display text-base font-semibold text-white shadow-button transition-colors hover:bg-yokoso-red-dark"
        >
          {isLocationSlide ? t('onboarding.allowLocation') : t('onboarding.next')}
        </button>

        <button
          onClick={isLocationSlide ? () => answerLocation(false) : finishOnboarding}
          className="mt-3 block w-full text-center font-body text-sm text-text-tertiary transition-colors hover:text-text-secondary"
        >
          {isLocationSlide ? t('onboarding.later') : t('onboarding.skip')}
        </button>
      </div>
    </div>
  )
}
